import { z } from 'zod';
import { createPaginatedResponseSchema, IdParamsSchema, MetadataSchema, PaginationQuerySchema, } from '../common/index.js';
import { UserProfileSchema } from '../users/index.js';
export const OnboardingStatusEnum = z.enum([
    'not_started',
    'in_progress',
    'completed',
]);
export const TenantSchema = z.object({
    id: z.string(),
    name: z.string().min(1).max(100),
    slug: z
        .string()
        .min(3)
        .max(50)
        .regex(/^[a-z0-9_-]+$/, 'Slug must be lowercase alphanumeric with hyphens/underscores'),
    owner_id: z.string().nullable().optional(),
    is_personal: z.boolean().default(false),
    onboarding_status: OnboardingStatusEnum.default('not_started'),
    company_size: z
        .enum(['1-10', '11-50', '51-200', '201-500', '500+'])
        .optional(),
    industry: z.string().max(100).optional(),
    website: z.string().url().optional(),
    logo_url: z.string().url().nullable().optional(),
    primary_color: z
        .string()
        .regex(/^#[0-9A-Fa-f]{6}$/)
        .optional(),
    timezone: z.string().default('UTC'),
    currency: z.string().length(3).default('USD'),
    language: z.string().default('en'),
    status: z.enum(['active', 'suspended', 'deleted']).default('active'),
    metadata: MetadataSchema.optional(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const UserTenantSchema = z.object({
    id: z.string().uuid(),
    user_id: z.string().uuid(),
    tenant_id: z.string().uuid(),
    role: z.enum(['owner', 'admin', 'member']).default('member'),
    is_default: z.boolean().default(false),
    joined_at: z.string().datetime(),
    last_accessed_at: z.string().datetime().nullable().optional(),
    metadata: MetadataSchema.optional(),
});
export const TenantMemberSchema = UserTenantSchema.extend({
    user: UserProfileSchema,
});
export const CreateTenantSchema = z.object({
    name: z.string().min(1, 'Tenant name is required').max(100),
    slug: z
        .string()
        .min(3, 'Slug must be at least 3 characters')
        .max(50)
        .regex(/^[a-z0-9-]+$/, 'Slug must be lowercase alphanumeric with hyphens'),
    is_personal: z.boolean().default(false),
    company_size: z
        .enum(['1-10', '11-50', '51-200', '201-500', '500+'])
        .optional(),
    industry: z.string().max(100).optional(),
    website: z.string().url().optional(),
    logo_url: z.string().url().optional(),
    primary_color: z
        .string()
        .regex(/^#[0-9A-Fa-f]{6}$/)
        .optional(),
    timezone: z.string().default('UTC').optional(),
    currency: z.string().length(3).default('USD').optional(),
    language: z.string().default('en').optional(),
    metadata: MetadataSchema.optional(),
});
export const UpdateTenantSchema = z.object({
    name: z.string().min(1).max(100).optional(),
    slug: z
        .string()
        .min(3)
        .max(50)
        .regex(/^[a-z0-9-]+$/, 'Slug must be lowercase alphanumeric with hyphens')
        .optional(),
    company_size: z
        .enum(['1-10', '11-50', '51-200', '201-500', '500+'])
        .optional(),
    industry: z.string().max(100).optional().nullable(),
    website: z.string().url().optional().nullable(),
    logo_url: z.string().url().optional().nullable(),
    primary_color: z
        .string()
        .regex(/^#[0-9A-Fa-f]{6}$/)
        .optional()
        .nullable(),
    timezone: z.string().optional(),
    currency: z.string().length(3).optional(),
    language: z.string().optional(),
    status: z.enum(['active', 'suspended', 'deleted']).optional(),
    metadata: MetadataSchema.optional(),
});
export const AddMemberToTenantSchema = z.object({
    user_id: z.string().uuid(),
    role: z.enum(['admin', 'member']).default('member'),
    metadata: MetadataSchema.optional(),
});
export const UpdateMemberRoleSchema = z.object({
    role: z.enum(['owner', 'admin', 'member']),
});
export const RemoveMemberFromTenantSchema = z.object({
    user_id: z.string().uuid(),
    reason: z.string().max(200).optional(),
});
export const SwitchTenantSchema = z.object({
    tenant_id: z.string(),
});
export const SetDefaultTenantSchema = z.object({
    tenant_id: z.string(),
});
export const OnboardingStatusResponseSchema = z.object({
    onboarding_status: OnboardingStatusEnum,
    needs_onboarding: z.boolean(),
    tenant_id: z.string(),
});
export const CompleteTenantOnboardingSchema = z.object({
    tenant_name: z
        .string()
        .min(2, 'Tenant name must be at least 2 characters')
        .max(100),
});
export const TenantQuerySchema = PaginationQuerySchema.extend({
    search: z.string().optional(),
    is_personal: z.boolean().optional(),
    status: z.enum(['active', 'suspended', 'deleted']).optional(),
    owner_id: z.string().uuid().optional(),
    sort: z
        .enum(['name', 'created_at', 'updated_at'])
        .default('created_at')
        .optional(),
    order: z.enum(['asc', 'desc']).default('desc').optional(),
});
export const TenantMemberQuerySchema = PaginationQuerySchema.extend({
    tenant_id: z.string().uuid(),
    role: z.enum(['owner', 'admin', 'member']).optional(),
    search: z.string().optional(),
    sort: z
        .enum(['name', 'email', 'joined_at', 'role'])
        .default('joined_at')
        .optional(),
    order: z.enum(['asc', 'desc']).default('desc').optional(),
});
export const TenantResponseSchema = TenantSchema.extend({
    member_count: z.number().int().min(0).optional(),
    environment_count: z.number().int().min(0).optional(),
});
export const TenantWithRoleSchema = TenantResponseSchema.extend({
    user_role: z.enum(['owner', 'admin', 'member']),
    is_default: z.boolean().optional(),
    default_environment_id: z.string().nullable().optional(),
});
export const SwitchTenantResponseSchema = z.object({
    tenant: TenantResponseSchema,
    message: z.string(),
    success: z.boolean(),
});
export const PaginatedTenantsResponseSchema = createPaginatedResponseSchema(TenantWithRoleSchema);
export const PaginatedTenantMembersResponseSchema = createPaginatedResponseSchema(TenantMemberSchema);
export const TenantParamsSchema = IdParamsSchema;
export const TenantMemberParamsSchema = z.object({
    tenantId: z.string().uuid(),
    userId: z.string().uuid(),
});
//# sourceMappingURL=index.js.map